import { SessionContext } from '@/context/SessionContext';
import { Song } from '@/javascript/library-objects';
import {
	ActionIcon,
	Box, Center,
	Group,
	keys, Pagination, ScrollArea,
	Table,
	Text,
	TextInput,
	UnstyledButton,
	useMantineTheme
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowUp, IconChevronDown, IconChevronUp, IconSearch, IconSelector } from '@tabler/icons-react';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';


interface RowData {
	name: string;
	artist: string;
	measures: number;
	drags: number;
	previews: number;
	performances: number;
}

interface ThProps {
	children: React.ReactNode;
	reversed: boolean;
	sorted: boolean;
	onSort: () => void;
}

function Th({ children, reversed, sorted, onSort }: ThProps) {
	const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
	return (
		<Table.Th>
			<UnstyledButton onClick={onSort}>
				<Group justify="space-between">
					<Text fw={500} fz="sm">
						{children}
					</Text>
					<Center>
						<Icon size={16} stroke={1.5} />
					</Center>
				</Group>
			</UnstyledButton>
		</Table.Th>
	);
}

function filterData(data: RowData[], search: string) {
	const query = search.toLowerCase().trim();
	return data.filter((item) =>
		keys(data[0]).some((key) => String(item[key]).toLowerCase().includes(query))
	);
}

function sortData(
	data: RowData[],
	payload: { sortBy: keyof RowData | null; reversed: boolean; search: string }
) {
	const { sortBy } = payload;

	if (!sortBy) {
		return filterData(data, payload.search);
	}

	return filterData(
		[...data].sort((a, b) => {
			if (typeof a[sortBy] === 'string' && typeof b[sortBy] === 'string') {
				if (payload.reversed) {
					return (b[sortBy] as string).localeCompare(a[sortBy] as string, undefined, { numeric: true });
				}
				return (a[sortBy] as string).localeCompare(b[sortBy] as string, undefined, { numeric: true });
			}
			// If numbers, sort numerically
			if (typeof a[sortBy] === 'number' && typeof b[sortBy] === 'number') {
				return payload.reversed
					? (b[sortBy] as number) - (a[sortBy] as number)
					: (a[sortBy] as number) - (b[sortBy] as number);
			}
			return 0;
		}),
		payload.search
	);
}

// Map Song to RowData
function mapSongToRowData(song: Song): RowData {
	return {
		name: song.songName,
		artist: song.artists[0].artistName,
		measures: song.submissionUsage
			? song.submissionUsage.reduce((sum, submission) => sum + (submission.measuresUsed || 0), 0)
			: 0,
		drags: song.drags
			? song.drags.reduce((sum, drag) => sum + (drag.measureCount || 0), 0)
			: 0,
		previews: song.previews.length,
		performances: song.submissionUsage.length,
	};
}

const Catalog: React.FC = () => {
	const theme = useMantineTheme();
	const session = useContext(SessionContext);

	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const [search, setSearch] = useState('');
	const [sortedData, setSortedData] = useState<RowData[]>([]);
	const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
	const [reverseSortDirection, setReverseSortDirection] = useState(false);
	const [activePage, setActivePage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	if (!session) {
		return null;
	}

	const artistStats = session.artistStats;

	const songs: Song[] = artistStats?.songs || [];

	const allRowData: RowData[] = songs.map(mapSongToRowData);

	const ROW_HEIGHT = isMobile ? 20 : 48;
	const CATALOG_HEIGHT = isMobile ? 300 : 600;
	const scrollAreaRef = useRef<HTMLDivElement>(null);


	useEffect(() => {
		setSortedData(sortData(allRowData, { sortBy, reversed: reverseSortDirection, search }));
		setActivePage(1);
	}, [artistStats?.songs]);

	const setSorting = (field: keyof RowData) => {
		const reversed = field === sortBy ? !reverseSortDirection : false;
		setReverseSortDirection(reversed);
		setSortBy(field);
		const sorted = sortData(allRowData, { sortBy: field, reversed, search });
		setSortedData(sorted);
		setActivePage(1);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.currentTarget;
		setSearch(value);
		const sorted = sortData(allRowData, { sortBy, reversed: reverseSortDirection, search: value });
		setSortedData(sorted);
		setActivePage(1);
	};

	useLayoutEffect(() => {
		function calculateRowsPerPage() {
			if (scrollAreaRef.current) {
				const totalHeight = CATALOG_HEIGHT;
				const headerHeight = 48;
				const searchHeight = 64;
				const paginationHeight = 80;
				const available = totalHeight - headerHeight - searchHeight - paginationHeight - 32;
				const rows = Math.max(1, Math.floor(available / ROW_HEIGHT));
				setRowsPerPage(rows);
			}
		}
		calculateRowsPerPage();
		window.addEventListener('resize', calculateRowsPerPage);
		return () => window.removeEventListener('resize', calculateRowsPerPage);
	}, [CATALOG_HEIGHT, ROW_HEIGHT, isMobile]);

	const total = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
	const pagedRows = sortedData.slice((activePage - 1) * rowsPerPage, activePage * rowsPerPage);

	const rows = pagedRows.map((row) => (
		<Table.Tr key={row.name + row.artist}>
			<Table.Td fz={isMobile ? 10 : 14}>{row.name}</Table.Td>
			<Table.Td fz={isMobile ? 10 : 14}>{row.artist}</Table.Td>
			<Table.Td fz={isMobile ? 10 : 14}>{row.measures}</Table.Td>
			<Table.Td fz={isMobile ? 10 : 14}>{row.drags}</Table.Td>
			<Table.Td fz={isMobile ? 10 : 14}>{row.previews}</Table.Td>
			<Table.Td fz={isMobile ? 10 : 14}>{row.performances}</Table.Td>
		</Table.Tr>
	));

	return (
		<Box
			w={"110%"}
			h={CATALOG_HEIGHT}
			style={{
				position: "relative",
				minHeight: 400,
				maxHeight: '80vh',
				background: theme.colors.blue[0],
				borderRadius: 16,
				boxSizing: "border-box",
				overflow: "hidden",
				justifySelf: "center"
			}}
		>
			<ScrollArea
				bg={theme.colors.blue[0]}
				style={{
					height: "100%",
					paddingBottom: 80,
				}}
				viewportRef={scrollAreaRef}
			>
				<Box w="100%" display="flex" style={{ justifyContent: "center" }}>
					<TextInput
						placeholder="Search by any field"
						mb="md"
						w="50%"
						mt={10}
						leftSection={<IconSearch size={16} stroke={1.5} />}
						value={search}
						onChange={handleSearchChange}
					/>
				</Box>
				<Table horizontalSpacing="md" verticalSpacing="xs" miw={600} layout="fixed">
					<Table.Tbody>
						<Table.Tr>
							<Th
								sorted={sortBy === 'name'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('name')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Name
								</Text>
							</Th>
							<Th
								sorted={sortBy === 'artist'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('artist')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Artist
								</Text>
							</Th>
							<Th
								sorted={sortBy === 'measures'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('measures')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Measures Submitted
								</Text>
							</Th>
							<Th
								sorted={sortBy === 'drags'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('drags')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Measures Dragged
								</Text>
							</Th>
							<Th
								sorted={sortBy === 'previews'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('previews')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Previews
								</Text>
							</Th>
							<Th
								sorted={sortBy === 'performances'}
								reversed={reverseSortDirection}
								onSort={() => setSorting('performances')}
							>
								<Text fz={isMobile ? 10 : 14}>
									Performances
								</Text>
							</Th>
						</Table.Tr>
					</Table.Tbody>
					<Table.Tbody>
						{rows.length > 0 ? (
							rows
						) : (
							<Table.Tr>
								<Table.Td colSpan={6}>
									<Text fw={500} ta="center">
										Nothing found
									</Text>
								</Table.Td>
							</Table.Tr>
						)}
					</Table.Tbody>
				</Table>
			</ScrollArea>
			<Box
				pos={"absolute"}
				left={0}
				right={0}
				bottom={0}
				bg={theme.colors.blue[0]}
				display={"flex"}
				h={isMobile ? 30 : 64}
				style={{
					justifyContent: "center",
					alignItems: "center",
					zIndex: 20,
				}}
			>
				<Pagination
					total={total}
					value={activePage}
					onChange={setActivePage}
					size={isMobile ? 12 : "sm"}
					radius="md"
					withEdges
				/>
			</Box>
			<ActionIcon
				bg={theme.colors.blue[0]}
				c={theme.colors.blue[8]}
				onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
				pos={"absolute"}
				right={24}
				bottom={20}
				style={{
					zIndex: 21
				}}
			>
				<IconArrowUp />
			</ActionIcon>
		</Box>
	);
};

export default Catalog;